import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { SelectOption, SnackbarService } from '@bcl/design-system';
import { BarcodeFormat } from '@zxing/library';
import { Subject, takeUntil, filter } from 'rxjs';
import { ScannerService } from 'src/app/services/scanner.service';
import { Monitor, MonitorWithOptions } from '../../area.model';
import { AreaApiService } from '../../area.service';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { BaseAreaAccordionComponent } from '../base-accordion.component';
import { AccordionService } from 'src/app/services/accordion.service';
import { MatDialog } from '@angular/material/dialog';
import {
  ObservationQuestionsDialogComponent,
  ObservationQuestionsDialogData,
  ObservationQuestionsDialogResult,
} from './observation-questions-dialog/observation-questions-dialog.component';

interface FiltersForm {
  showUnscannedOnly: FormControl<boolean>;
  moduleId: FormControl<string | null>;
  selectedInstrumentType: FormControl<string | null>;
  toxic: FormControl<string>;
  temporary: FormControl<string>;
  outside: FormControl<string>;
  checkStatus: FormControl<string>;
}

@Component({
  selector: 'app-area-details-monitors',
  templateUrl: './area-details-monitors.component.html',
  styleUrls: ['./area-details-monitors.component.scss'],
})
export class AreaDetailsMonitorsComponent
  extends BaseAreaAccordionComponent
  implements OnInit, OnDestroy
{
  @Input() area;
  @Input() visitId: number;
  @Input() date: string;
  @Input() dayNumber: string;
  @Input() areaId: number;
  @Input() instrumentTypes: SelectOption[];
  @Input() moduleTypes: SelectOption[] = [];
  @Input() visit?: any; // Add visit input
  @Input() isEditingDisabled = false;
  @Input() customBackUrl?: string; // Custom back URL for navigation
  @Input() externalFilters?: {
    moduleId?: string;
    instrumentType?: string;
    toxic?: string;
    temporary?: string;
    outside?: string;
    showUnscannedOnly?: boolean;
    checkStatus?: string;
  }; // External filter values
  @Input() useExternalFilters = false; // Flag to determine if using external filters

  @Output() observationChange = new EventEmitter<ObservationMonitorDetails>();
  @Output() refreshMonitorsData = new EventEmitter<Monitor[]>();
  @Output() saveFields = new EventEmitter();
  @Output() filtersChanged = new EventEmitter<{
    moduleId?: string;
    instrumentType?: string;
    toxic: string;
    temporary: string;
    outside: string;
    showUnscannedOnly: boolean;
    checkStatus: string;
  }>();

  public showItemList = true;
  public showFilters = false;
  public unscannedCount = 0;
  public scannedCount = 0;
  public unreachableCount = 0;
  public filteredMonitors: MonitorWithOptions[] = [];
  public filtersForm: FormGroup<FiltersForm>;
  public allowedFormats: BarcodeFormat[] = [
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.CODE_128,
    BarcodeFormat.EAN_8,
    BarcodeFormat.EAN_13,
    BarcodeFormat.QR_CODE,
  ];
  public isProcessing = false; // Track when monitor observation changes are in progress
  public moduleOptions: SelectOption[] = [];
  public instrumentTypeOptions: SelectOption[] = [];
  public toxicOptions: SelectOption[] = [];
  public temporaryOptions: SelectOption[] = [];
  public outsideOptions: SelectOption[] = [];
  public checkStatusOptions: SelectOption[] = [];

  private monitorsList: Array<MonitorWithOptions> = [];
  private destroy$ = new Subject<void>();
  private previousUrl = '';
  private lastFilterState = false;
  private lastInstrumentType: string | null = null;
  private lastToxicFilter = 'all';
  private lastTemporaryFilter = 'all';
  private lastOutsideFilter = 'all';
  private lastCheckStatusFilter = 'all';
  private windowWidth = window.innerWidth;

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackbarService: SnackbarService,
    private areaApiService: AreaApiService,
    private fb: FormBuilder,
    accordionService: AccordionService,
    private dialog: MatDialog
  ) {
    super(accordionService);
    this.filtersForm = this.fb.group<FiltersForm>({
      showUnscannedOnly: this.fb.control(false),
      moduleId: this.fb.control(null),
      selectedInstrumentType: this.fb.control(null),
      toxic: this.fb.control('all'),
      temporary: this.fb.control('all'),
      outside: this.fb.control('all'),
      checkStatus: this.fb.control('all'),
    });
  }

  // Computed property to check if form should be disabled
  get isFormDisabled(): boolean {
    return this.isEditingDisabled;
  }

  get showUnscannedOnlyControl(): FormControl<boolean> {
    return this.filtersForm.get('showUnscannedOnly') as FormControl<boolean>;
  }

  get selectedInstrumentTypeControl(): FormControl<string | null> {
    return this.filtersForm.get('selectedInstrumentType') as FormControl<
      string | null
    >;
  }

  get moduleIdControl(): FormControl<string | null> {
    return this.filtersForm.get('moduleId') as FormControl<string | null>;
  }

  get toxicControl(): FormControl<string> {
    return this.filtersForm.get('toxic') as FormControl<string>;
  }

  get temporaryControl(): FormControl<string> {
    return this.filtersForm.get('temporary') as FormControl<string>;
  }

  get outsideControl(): FormControl<string> {
    return this.filtersForm.get('outside') as FormControl<string>;
  }

  get checkStatusControl(): FormControl<string> {
    return this.filtersForm.get('checkStatus') as FormControl<string>;
  }

  get mappedMonitors(): Array<MonitorWithOptions> {
    return this.monitorsList;
  }

  @Input() set mappedMonitors(value: Array<MonitorWithOptions>) {
    if (value) {
      this.monitorsList = value;
      this.updateFilterOptions();
      this.updateMonitorsList();
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Initialize boolean filter options only (module/instrument options are updated when monitors are set)

    this.toxicOptions = [
      { label: 'AREA.MAP.LABELS.ALL', value: 'all' },
      { label: 'AREA.MAP.LABELS.YES', value: 'yes' },
      { label: 'AREA.MAP.LABELS.NO', value: 'no' },
    ];
    this.temporaryOptions = [
      { label: 'AREA.MAP.LABELS.ALL', value: 'all' },
      { label: 'AREA.MAP.LABELS.YES', value: 'yes' },
      { label: 'AREA.MAP.LABELS.NO', value: 'no' },
    ];
    this.outsideOptions = [
      { label: 'AREA.MAP.LABELS.ALL', value: 'all' },
      { label: 'AREA.MAP.LABELS.YES', value: 'yes' },
      { label: 'AREA.MAP.LABELS.NO', value: 'no' },
    ];
    this.checkStatusOptions = [
      { label: 'AREA.MAP.LABELS.ALL', value: 'all' },
      { label: 'AREA.MAP.LABELS.YES', value: 'yes' },
      { label: 'AREA.MAP.LABELS.NO', value: 'no' },
    ];

    // Update filter options now that we have all the data
    if (this.monitorsList && (this.moduleTypes || this.instrumentTypes)) {
      this.updateFilterOptions();
    }

    // Try to restore the last filter states from localStorage
    const savedUnscannedState = localStorage.getItem(
      `unscannedOnly_${this.areaId}`
    );
    const savedModuleId = localStorage.getItem(`moduleId_${this.areaId}`);
    const savedInstrumentType = localStorage.getItem(
      `instrumentType_${this.areaId}`
    );
    const savedToxicFilter = localStorage.getItem(`toxicFilter_${this.areaId}`);
    const savedTemporaryFilter = localStorage.getItem(
      `temporaryFilter_${this.areaId}`
    );
    const savedOutsideFilter = localStorage.getItem(
      `outsideFilter_${this.areaId}`
    );
    const savedCheckStatusFilter = localStorage.getItem(
      `checkStatusFilter_${this.areaId}`
    );

    // Initialize form with saved values or defaults
    const formValues = {
      showUnscannedOnly:
        this.useExternalFilters &&
        this.externalFilters?.showUnscannedOnly !== undefined
          ? this.externalFilters.showUnscannedOnly
          : savedUnscannedState === 'true',
      moduleId:
        this.useExternalFilters && this.externalFilters?.moduleId
          ? this.externalFilters.moduleId
          : savedModuleId?.toString()
          ? savedModuleId
          : null,
      selectedInstrumentType:
        this.useExternalFilters && this.externalFilters?.instrumentType
          ? this.externalFilters.instrumentType
          : savedInstrumentType?.toString()
          ? savedInstrumentType
          : null,
      toxic:
        this.useExternalFilters && this.externalFilters?.toxic
          ? this.externalFilters.toxic
          : savedToxicFilter || 'all',
      temporary:
        this.useExternalFilters && this.externalFilters?.temporary
          ? this.externalFilters.temporary
          : savedTemporaryFilter || 'all',
      outside:
        this.useExternalFilters && this.externalFilters?.outside
          ? this.externalFilters.outside
          : savedOutsideFilter || 'all',
      checkStatus: savedCheckStatusFilter || 'all',
    };

    this.filtersForm.setValue(formValues, { emitEvent: true });

    // Save initial states
    this.lastFilterState = this.filtersForm.get('showUnscannedOnly').value;
    this.lastInstrumentType = this.filtersForm.get(
      'selectedInstrumentType'
    ).value;
    this.lastToxicFilter = this.filtersForm.get('toxic').value;
    this.lastTemporaryFilter = this.filtersForm.get('temporary').value;
    this.lastOutsideFilter = this.filtersForm.get('outside').value;
    this.lastCheckStatusFilter = this.filtersForm.get('checkStatus').value;

    // Subscribe to form changes
    this.filtersForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterMonitors();

        if (this.useExternalFilters) {
          const { showUnscannedOnly } = this.filtersForm.value;
          const currentAreaMapFilters = this.externalFilters || {};

          this.filtersChanged.emit({
            moduleId: currentAreaMapFilters.moduleId || '',
            instrumentType: currentAreaMapFilters.instrumentType || '',
            toxic: currentAreaMapFilters.toxic || 'all',
            temporary: currentAreaMapFilters.temporary || 'all',
            outside: currentAreaMapFilters.outside || 'all',
            showUnscannedOnly,
            checkStatus: currentAreaMapFilters.checkStatus || 'all',
          });
        } else {
          const {
            moduleId,
            selectedInstrumentType,
            toxic,
            temporary,
            outside,
            showUnscannedOnly,
            checkStatus,
          } = this.filtersForm.value;

          this.filtersChanged.emit({
            moduleId: moduleId || '',
            instrumentType: selectedInstrumentType || '',
            toxic,
            temporary,
            outside,
            showUnscannedOnly,
            checkStatus,
          });
        }
      });

    if (this.mappedMonitors) {
      this.updateMonitorsList();
    }

    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        const currentUrl = event.url;
        const isReturningFromMonitor =
          this.previousUrl.includes('/area/monitor') &&
          currentUrl.includes(
            `/area/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`
          );
        this.previousUrl = currentUrl;

        if (isReturningFromMonitor) {
          // Restore the last filter states before refreshing
          this.filtersForm.patchValue({
            showUnscannedOnly: this.lastFilterState,
            selectedInstrumentType: this.lastInstrumentType,
          });
          this.refreshData();
        }
      });
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.onWindowResize.bind(this));
  }

  public toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  // Method to update filters from external source (like area-map)
  public updateExternalFilters(filters: {
    moduleId?: string;
    instrumentType?: string;
    toxic?: string;
    temporary?: string;
    outside?: string;
    showUnscannedOnly?: boolean;
    checkStatus?: string;
  }): void {
    if (this.useExternalFilters) {
      const currentValues = this.filtersForm.value;
      this.filtersForm.patchValue(
        {
          moduleId: filters.moduleId || currentValues.moduleId,
          selectedInstrumentType:
            filters.instrumentType || currentValues.selectedInstrumentType,
          toxic: filters.toxic || currentValues.toxic,
          temporary: filters.temporary || currentValues.temporary,
          outside: filters.outside || currentValues.outside,
          checkStatus: filters.checkStatus || currentValues.checkStatus,
          showUnscannedOnly:
            filters.showUnscannedOnly !== undefined
              ? filters.showUnscannedOnly
              : currentValues.showUnscannedOnly,
        },
        { emitEvent: false }
      ); // Don't emit to avoid circular updates

      this.updateMonitorsList();
    }
  }

  handleBarcode() {
    this.router.navigate([
      '/scanner',
      'area',
      this.date,
      this.visitId,
      this.dayNumber,
      this.areaId,
    ]);
  }

  shouldShowObservationButton(
    monitor: MonitorWithOptions,
    observationId: string
  ): boolean {
    const isAllowedByStatus =
      !monitor.statuses ||
      monitor.statuses.length === 0 ||
      monitor.statuses.some((status) => status === observationId);

    if (!isAllowedByStatus) {
      return false;
    }

    const allowedButtons = this.getAllowedButtonIds(monitor);

    const maxButtons = this.getMaxButtonsToShow();

    const buttonIndex = allowedButtons.indexOf(observationId);

    return buttonIndex >= 0 && buttonIndex < maxButtons;
  }

  goToMonitor(monitor: Monitor) {
    this.saveFields.emit();
    // Save the current filter states before navigation
    this.lastFilterState = this.filtersForm.get('showUnscannedOnly').value;
    this.lastInstrumentType = this.filtersForm.get(
      'selectedInstrumentType'
    ).value;

    // Only save to localStorage if not using external filters
    if (!this.useExternalFilters) {
      localStorage.setItem(
        `unscannedOnly_${this.areaId}`,
        this.filtersForm.get('showUnscannedOnly').value.toString()
      );
      localStorage.setItem(
        `instrumentType_${this.areaId}`,
        this.filtersForm.get('selectedInstrumentType').value?.toString() || ''
      );
    }

    // Use custom back URL if provided, otherwise use default area URL
    const backUrl =
      this.customBackUrl ||
      `/area/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`;

    this.router.navigate(
      [
        '/area/monitor',
        this.date,
        this.visitId,
        this.dayNumber,
        this.areaId,
        monitor.id,
      ],
      {
        state: {
          backUrl,
          filteredMonitors: this.filteredMonitors,
        },
      }
    );
  }

  filterMonitors() {
    const {
      showUnscannedOnly,
      moduleId,
      selectedInstrumentType,
      toxic,
      temporary,
      outside,
      checkStatus,
    } = this.filtersForm.value;

    // Save the new filter states
    this.lastFilterState = showUnscannedOnly;
    this.lastInstrumentType = selectedInstrumentType;
    this.lastToxicFilter = toxic;
    this.lastTemporaryFilter = temporary;
    this.lastOutsideFilter = outside;
    this.lastCheckStatusFilter = checkStatus;

    // Only save to localStorage if not using external filters
    // (when using external filters, the parent component manages localStorage)
    if (!this.useExternalFilters) {
      localStorage.setItem(
        `unscannedOnly_${this.areaId}`,
        showUnscannedOnly.toString()
      );
      localStorage.setItem(
        `moduleId_${this.areaId}`,
        moduleId?.toString() || ''
      );
      localStorage.setItem(
        `instrumentType_${this.areaId}`,
        selectedInstrumentType?.toString() || ''
      );
      localStorage.setItem(`toxicFilter_${this.areaId}`, toxic || 'all');
      localStorage.setItem(
        `temporaryFilter_${this.areaId}`,
        temporary || 'all'
      );
      localStorage.setItem(`outsideFilter_${this.areaId}`, outside || 'all');
      localStorage.setItem(
        `checkStatusFilter_${this.areaId}`,
        checkStatus || 'all'
      );
    }

    this.updateMonitorsList();
  }

  changeObservation(observationId: string, monitorId: number) {
    const targetMonitor = this.mappedMonitors.find((m) => m.id === monitorId);
    if (!targetMonitor) {
      return;
    }

    // If observationId is '1' (Change), open the observation questions modal
    if (observationId === '1') {
      this.openObservationQuestionsModal(targetMonitor);
      return;
    }

    this.isProcessing = true;

    targetMonitor.observation.id = Number(observationId);
    if (observationId === '2') {
      // If marking as OK
      targetMonitor.statusId = '2';
    }
    if (this.filtersForm.get('showUnscannedOnly').value) {
      this.filteredMonitors = this.filteredMonitors.filter(
        (m) => m.id !== monitorId
      );
    }
    this.updateMonitorsList();

    this.areaApiService
      .changeMonitorObservation(monitorId, this.visitId, {
        id: observationId,
        details: targetMonitor.observation.details,
      })
      .subscribe({
        next: () => {
          this.refreshData();
        },
        error: () => {
          if (targetMonitor) {
            targetMonitor.observation.id = 200;
            targetMonitor.statusId = '200';
            this.updateMonitorsList();
            this.snackbarService.open(
              'red',
              'MONITOR.DETAILS.MESSAGES.OBSERVATION_CHANGE_ERROR'
            );
          }
          this.refreshData();
        },
      });
  }

  private openObservationQuestionsModal(monitor: MonitorWithOptions) {
    // Check if monitor has observation questions
    if (
      !monitor.observationQuestions ||
      monitor.observationQuestions.length === 0
    ) {
      // No questions - save observation directly
      this.saveObservationDirectly(monitor, '1', {});
      return;
    }

    // Has questions - open modal
    const dialogData: ObservationQuestionsDialogData = {
      monitor,
    };

    const dialogRef = this.dialog.open(ObservationQuestionsDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: ObservationQuestionsDialogResult | null) => {
        if (result) {
          this.saveObservationDirectly(
            monitor,
            result.observationId,
            result.selectedAnswers
          );
        }
      });
  }

  private saveObservationDirectly(
    monitor: MonitorWithOptions,
    observationId: string,
    selectedAnswers: { [key: number]: string }
  ) {
    // Set processing state to disable all action buttons
    this.isProcessing = true;

    // Update the local monitor status for immediate visual feedback
    monitor.observation.id = Number(observationId);
    monitor.observation.details = JSON.stringify(selectedAnswers);
    monitor.statusId = observationId; // Update status immediately for color change

    // Update filtered list if needed
    if (this.filtersForm.get('showUnscannedOnly').value) {
      this.filteredMonitors = this.filteredMonitors.filter(
        (m) => m.id !== monitor.id
      );
    }
    // Force an immediate UI update
    this.updateMonitorsList();

    // Handle server update
    this.areaApiService
      .changeMonitorObservation(monitor.id, this.visitId, {
        id: observationId,
        details: JSON.stringify(selectedAnswers),
      })
      .subscribe({
        next: () => {
          // After server confirms, refresh to ensure we have latest state
          this.refreshData();
        },
        error: () => {
          // If server update fails, revert the local change
          monitor.observation.id = 200; // Reset to unscanned
          monitor.statusId = '200'; // Reset status for color
          this.updateMonitorsList();
          this.snackbarService.open(
            'red',
            'MONITOR.DETAILS.MESSAGES.OBSERVATION_CHANGE_ERROR'
          );
          this.refreshData();
        },
      });
  }

  private filterModuleOptions(selectedMonitors, allModuleOptions) {
    if (!selectedMonitors || selectedMonitors.length === 0) {
      return [];
    }
    const moduleIds = selectedMonitors.map((monitor) => monitor.moduleId);
    return allModuleOptions.filter((option) =>
      moduleIds.includes(option.value)
    );
  }

  private filterInstrumentTypeOptions(selectedMonitors, allTypeOptions) {
    if (!selectedMonitors || selectedMonitors.length === 0) {
      return [];
    }
    const typeIds = selectedMonitors.map((monitor) => monitor.typeId);
    return allTypeOptions.filter((option) => typeIds.includes(option.value));
  }

  private updateFilterOptions(): void {
    // Only update if we have data available
    if (this.monitorsList && this.monitorsList.length > 0) {
      // Update module options based on available monitors
      this.moduleOptions = this.filterModuleOptions(
        this.monitorsList,
        this.moduleTypes || []
      );

      // Update instrument type options based on available monitors
      this.instrumentTypeOptions = this.filterInstrumentTypeOptions(
        this.monitorsList,
        this.instrumentTypes || []
      );
    } else {
      // If no monitors available, use all available options
      this.moduleOptions = this.moduleTypes || [];
      this.instrumentTypeOptions = this.instrumentTypes || [];
    }
  }

  private refreshData() {
    if (!this.areaId || !this.visitId) {
      return;
    }

    this.areaApiService
      .getMonitors(this.areaId, this.visitId, {
        status: null,
        module: null,
        type: null,
      })
      .subscribe({
        next: (monitors) => {
          // Update parent first
          this.refreshMonitorsData.emit(monitors);

          // Update local state with fresh data
          this.mappedMonitors.forEach((monitor) => {
            const updatedMonitor = monitors.find((m) => m.id === monitor.id);
            if (updatedMonitor) {
              // Update all monitor properties
              Object.assign(monitor, {
                observation: updatedMonitor.observation,
                statusId: updatedMonitor.statusId,
                batteryStatus: updatedMonitor.batteryStatus,
                moduleId: updatedMonitor.moduleId,
                typeId: updatedMonitor.typeId,
                qrCode: updatedMonitor.qrCode,
              });
            }
          });

          // Update the UI
          this.updateMonitorsList();
          this.isProcessing = false;
        },
        error: () => {
          this.isProcessing = false;
        },
      });
  }

  private updateMonitorsList() {
    // When using external filters, get values from external filters instead of form
    let filterValues;
    if (this.useExternalFilters && this.externalFilters) {
      filterValues = {
        showUnscannedOnly: this.externalFilters.showUnscannedOnly || false,
        moduleId: this.externalFilters.moduleId || '',
        selectedInstrumentType: this.externalFilters.instrumentType || '',
        toxic: this.externalFilters.toxic || 'all',
        temporary: this.externalFilters.temporary || 'all',
        outside: this.externalFilters.outside || 'all',
        checkStatus: this.externalFilters.checkStatus || 'all',
      };
    } else {
      filterValues = this.filtersForm.value;
    }

    const {
      showUnscannedOnly,
      moduleId,
      selectedInstrumentType,
      toxic,
      temporary,
      outside,
      checkStatus,
    } = filterValues;

    // Start with all monitors
    this.filteredMonitors = [...this.mappedMonitors];

    // Apply unscanned filter
    if (showUnscannedOnly) {
      this.filteredMonitors = this.filteredMonitors.filter(
        (monitor) => monitor.observation.id === 200
      );
    }

    if (moduleId) {
      this.filteredMonitors = this.filteredMonitors.filter(
        (monitor) => monitor.moduleId === moduleId.toString()
      );
    }

    if (selectedInstrumentType) {
      this.filteredMonitors = this.filteredMonitors.filter(
        (monitor) => monitor.typeId === selectedInstrumentType.toString()
      );
    }

    // Apply boolean filters
    if (toxic && toxic !== 'all') {
      this.filteredMonitors = this.filteredMonitors.filter((monitor) => {
        const toxicMatch =
          toxic === 'yes' ? monitor.toxic === true : monitor.toxic !== true;
        return toxicMatch;
      });
    }

    if (temporary && temporary !== 'all') {
      this.filteredMonitors = this.filteredMonitors.filter((monitor) => {
        const temporaryMatch =
          temporary === 'yes'
            ? monitor.temporary === true
            : monitor.temporary !== true;
        return temporaryMatch;
      });
    }

    if (outside && outside !== 'all') {
      this.filteredMonitors = this.filteredMonitors.filter((monitor) => {
        const outsideMatch =
          outside === 'yes'
            ? monitor.outside === true
            : monitor.outside !== true;
        return outsideMatch;
      });
    }

    if (checkStatus && checkStatus !== 'all') {
      this.filteredMonitors = this.filteredMonitors.filter((monitor) => {
        const checkStatusMatch =
          checkStatus === 'yes'
            ? monitor.checkStatus === true
            : monitor.checkStatus !== true;
        return checkStatusMatch;
      });
    }

    // Update counters
    this.unscannedCount = this.mappedMonitors.filter(
      (monitor) => monitor.observation.id === 200
    ).length;
    this.scannedCount = this.mappedMonitors.filter(
      (monitor) => monitor.observation.id !== 200
    ).length;
    this.unreachableCount = this.mappedMonitors.filter(
      (monitor) => monitor.observation.id === 3
    ).length;

    // Force change detection
    this.filteredMonitors = [...this.filteredMonitors];
    this.monitorsList = [...this.mappedMonitors];
  }

  private onWindowResize(): void {
    const newWidth = window.innerWidth;
    if (
      (this.windowWidth < 500 && newWidth >= 500) ||
      (this.windowWidth >= 500 && newWidth < 500)
    ) {
      this.windowWidth = newWidth;
      this.filteredMonitors = [...this.filteredMonitors];
    }
  }

  private getAllowedButtonIds(monitor: MonitorWithOptions): string[] {
    const allButtonIds = ['2', '5', '1'];

    if (!monitor.statuses || monitor.statuses.length === 0) {
      return allButtonIds;
    }

    return allButtonIds.filter((id) => monitor.statuses.includes(id));
  }

  private getMaxButtonsToShow(): number {
    const width = window.innerWidth;

    if (width < 500) {
      return 1;
    }

    return 3;
  }
}

export type ObservationMonitorDetails = {
  id: string;
  monitorId: number;
};
